'use client';

import { Check, Copy, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared transient feedback state for copy buttons that own their async
 * clipboard work. Centralizes the copied/isCopying flag dance (with unmount
 * cleanup) so every copy button behaves identically.
 *
 * @param timeoutMs how long the success state lingers. Defaults to 2000ms.
 */
export function useCopyFeedback(timeoutMs = 2000) {
  const [copied, setCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const markCopied = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCopied(true);
    timerRef.current = setTimeout(() => setCopied(false), timeoutMs);
  }, [timeoutMs]);

  return { copied, isCopying, setIsCopying, markCopied };
}

type CopyButtonVariant = 'default' | 'subtle' | 'floating';
type CopyButtonSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<CopyButtonSize, string> = {
  sm: 'h-6 px-2 text-[11px]',
  md: 'h-7 px-2.5 text-xs',
  lg: 'h-8 px-3 text-sm',
};

const ICON_SIZES: Record<CopyButtonSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

const VARIANT_CLASSES: Record<CopyButtonVariant, string> = {
  default:
    'border border-neutral-200 hover:border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-600 hover:text-neutral-800',
  // Diagram-style hover-revealed pill: frosted, tiny, appears on group hover.
  floating:
    'bg-white/90 backdrop-blur-sm border border-neutral-200 hover:border-neutral-300 text-neutral-500 hover:text-neutral-700 px-2 py-1 text-[10px] opacity-0 group-hover:opacity-100 focus:opacity-100',
  subtle:
    'opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700 border border-transparent hover:border-neutral-200 px-2 py-1 text-[11px]',
};

export interface CopyButtonBaseProps {
  /** Runs the clipboard write. Throwing signals failure (no success flash). */
  onCopy: () => Promise<void> | void;
  /** External feedback flags, e.g. from `useCopyMessage` or `useCopyFeedback`. */
  isCopying?: boolean;
  copied?: boolean;
  /** Notified after a successful copy so owners can flash their own state. */
  onCopied?: () => void;
  variant?: CopyButtonVariant;
  size?: CopyButtonSize;
  label?: string;
  copiedLabel?: string;
  copyingLabel?: string;
  className?: string;
  title?: string;
  disabled?: boolean;
}

/**
 * The single copy-button renderer for the app: icon + label swap with a
 * tactile press (`active:scale-95`), a shimmer sweep while the clipboard
 * write is in flight, and a green glow + popping check on success.
 *
 * Variants cover every existing placement — the bordered bubble-chat pill
 * (`default`), the hover-revealed ghost (`subtle`), and the frosted
 * diagram/table overlay (`floating`) — so call sites share visuals instead
 * of re-implementing the Copy/Loader/Check swap.
 */
export function CopyButtonBase({
  onCopy,
  isCopying: isCopyingProp,
  copied: copiedProp,
  onCopied,
  variant = 'default',
  size = 'sm',
  label = 'Salin',
  copiedLabel = 'Disalin',
  copyingLabel = 'Menyalin...',
  className,
  title,
  disabled,
}: CopyButtonBaseProps) {
  const feedback = useCopyFeedback();
  const isControlled = isCopyingProp !== undefined || copiedProp !== undefined;
  const isCopying = isCopyingProp ?? feedback.isCopying;
  const copied = copiedProp ?? feedback.copied;

  // Failures propagate to the owner (toast/logger) instead of flashing a
  // false success; `finally` just drops the shimmer so the button is idle.
  const handleClick = useCallback(async () => {
    if (isCopying) return;
    if (isControlled) {
      await onCopy();
      onCopied?.();
      return;
    }
    feedback.setIsCopying(true);
    try {
      await onCopy();
      feedback.markCopied();
      onCopied?.();
    } finally {
      feedback.setIsCopying(false);
    }
  }, [isCopying, isControlled, feedback, onCopy, onCopied]);

  const iconSize = ICON_SIZES[size];

  return (
    <button
      onClick={() => void handleClick()}
      disabled={disabled ?? isCopying}
      title={title ?? (copied ? copiedLabel : label)}
      className={cn(
        'relative flex items-center gap-1.5 rounded-md font-medium overflow-hidden',
        'transition-all duration-200 active:scale-95',
        VARIANT_CLASSES[variant],
        variant === 'default' && SIZE_CLASSES[size],
        isCopying && 'opacity-70 cursor-wait',
        copied && 'opacity-100 border-green-300 bg-green-50 text-green-600 hover:bg-green-50 hover:text-green-600 hover:border-green-300 shadow-[0_0_0_3px_rgba(34,197,94,0.12)]',
        className,
      )}
    >
      {isCopying && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-neutral-300/40 to-transparent bg-[length:200%_100%]"
        />
      )}
      {isCopying ? (
        <>
          <Loader2 size={iconSize} className="animate-spin shrink-0" />
          <span className="animate-pulse">{copyingLabel}</span>
        </>
      ) : copied ? (
        <>
          <Check size={iconSize} className="animate-scale-in shrink-0" />
          <span>{copiedLabel}</span>
        </>
      ) : (
        <>
          <Copy size={iconSize} className="shrink-0" />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

interface CopyButtonProps {
  text: string;
  html?: string;
  className?: string;
  label?: string;
  copiedLabel?: string;
  size?: CopyButtonSize;
  variant?: 'default' | 'subtle';
  onCopy?: () => Promise<void> | void;
}

/**
 * Self-contained text/html copy button. Prefer `CopyButtonBase` with
 * `useCopyMessage` when the payload needs DOM harvesting (rich chat copy);
 * this covers the simple `navigator.clipboard` cases.
 */
export function CopyButton({
  text,
  html,
  className,
  label = 'Salin',
  copiedLabel = 'Disalin',
  size = 'sm',
  variant = 'default',
  onCopy,
}: CopyButtonProps) {
  const handleCopy = useCallback(async () => {
    if (onCopy) {
      await onCopy();
      return;
    }
    if (html) {
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([text], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(text);
    }
  }, [text, html, onCopy]);

  return (
    <CopyButtonBase
      onCopy={handleCopy}
      variant={variant}
      size={size}
      label={label}
      copiedLabel={copiedLabel}
      className={className}
    />
  );
}

interface CopyImageButtonProps {
  className?: string;
  label?: string;
  copiedLabel?: string;
  onCopy: () => Promise<void>;
}

/** Self-contained image copy button (e.g. diagram PNG snapshots). */
export function CopyImageButton({
  className,
  label = 'Salin Gambar',
  copiedLabel = 'Disalin',
  onCopy,
}: CopyImageButtonProps) {
  return (
    <CopyButtonBase
      onCopy={onCopy}
      size="md"
      label={label}
      copiedLabel={copiedLabel}
      className={cn('px-2.5 py-1.5 text-xs', className)}
    />
  );
}
